const ICON_PATH = "/icons/icon-192.png"
const CANVAS_SIZE = 64
const BADGE_RADIUS_RATIO = 0.3
const BADGE_MARGIN_RATIO = 0.05
const MAX_LABEL_COUNT = 9
const MAX_APP_BADGE = 99
const BADGE_BG_COLOR = "#ef4444"
const BADGE_BORDER_COLOR = "#ffffff"

type BadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>
  clearAppBadge?: () => Promise<void>
}

let originalIconHref: string | null = null
let iconImagePromise: Promise<HTMLImageElement | null> | null = null
let lastAppliedTotal: number | null = null
let updateSequence = 0

export function getBadgeLabel(total: number): string | null {
  if (!Number.isFinite(total) || total <= 0) return null
  if (total > MAX_LABEL_COUNT) return `${MAX_LABEL_COUNT}+`
  return String(total)
}

function getIconImage(): Promise<HTMLImageElement | null> {
  if (!iconImagePromise) {
    iconImagePromise = new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = ICON_PATH
    })
  }
  return iconImagePromise
}

function getOriginalIconHref(): string {
  if (originalIconHref) return originalIconHref
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  originalIconHref = link?.href || ICON_PATH
  return originalIconHref
}

function setFaviconHref(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement("link")
    link.rel = "icon"
    link.type = "image/png"
    link.setAttribute("sizes", "192x192")
    document.head.appendChild(link)
  }
  if (link.href === href) return
  link.href = href
}

async function drawFaviconDataUrl(label: string): Promise<string | null> {
  const image = await getIconImage()
  const canvas = document.createElement("canvas")
  canvas.width = CANVAS_SIZE
  canvas.height = CANVAS_SIZE
  const context = canvas.getContext("2d")
  if (!context) return null
  context.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
  if (image) context.drawImage(image, 0, 0, CANVAS_SIZE, CANVAS_SIZE)

  const radius = CANVAS_SIZE * BADGE_RADIUS_RATIO
  const centerX = CANVAS_SIZE - radius - CANVAS_SIZE * BADGE_MARGIN_RATIO
  const centerY = CANVAS_SIZE - radius - CANVAS_SIZE * BADGE_MARGIN_RATIO
  context.beginPath()
  context.arc(centerX, centerY, radius, 0, Math.PI * 2)
  context.fillStyle = BADGE_BG_COLOR
  context.fill()
  context.lineWidth = Math.max(1, CANVAS_SIZE * 0.03)
  context.strokeStyle = BADGE_BORDER_COLOR
  context.stroke()

  const fontSize = label.length > 1 ? radius * 1.1 : radius * 1.3
  context.font = `bold ${fontSize}px sans-serif`
  context.textAlign = "center"
  context.textBaseline = "middle"
  context.fillStyle = BADGE_BORDER_COLOR
  context.fillText(label, centerX, centerY + fontSize * 0.05)
  return canvas.toDataURL("image/png")
}

export async function updateFaviconBadge(total: number): Promise<void> {
  if (lastAppliedTotal === total) return
  const sequence = ++updateSequence

  if (typeof navigator !== "undefined") {
    const badgeNavigator = navigator as BadgeNavigator
    if (
      typeof badgeNavigator.setAppBadge === "function" &&
      typeof badgeNavigator.clearAppBadge === "function"
    ) {
      try {
        if (total <= 0) {
          await badgeNavigator.clearAppBadge()
        } else {
          await badgeNavigator.setAppBadge(Math.min(total, MAX_APP_BADGE))
        }
      } catch {}
    }
  }

  try {
    if (typeof document === "undefined" || typeof Image === "undefined") return
    getOriginalIconHref()
    const label = getBadgeLabel(total)
    if (label === null) {
      setFaviconHref(getOriginalIconHref())
    } else {
      const dataUrl = await drawFaviconDataUrl(label)
      if (sequence !== updateSequence) return
      setFaviconHref(dataUrl ?? getOriginalIconHref())
    }
    lastAppliedTotal = total
  } catch {}
}
